/**
 * IntegrationsTab - Meta Ads connection management
 */

import {
  Link2,
  Link2Off,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AccountSelectorModal } from '@/components/meta';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import {
  useMetaConnection,
  useConnectMeta,
  useDisconnectMeta,
  useMetaAccounts,
  useFinalizeMetaConnection,
  useCancelPendingSession,
} from '@/hooks/useMetaConnection';
import { cn } from '@/lib/utils';
import type { MetaConnectionStatus } from '@/types/meta';

interface IntegrationsTabProps {
  teamId: string;
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: MetaConnectionStatus }) {
  const config = {
    active: {
      icon: CheckCircle2,
      label: 'Connected',
      className: 'bg-success/10 text-success',
    },
    expired: {
      icon: Clock,
      label: 'Expired',
      className: 'bg-warning/10 text-warning',
    },
    revoked: {
      icon: XCircle,
      label: 'Disconnected',
      className: 'bg-surface-subtle text-muted',
    },
    error: {
      icon: AlertCircle,
      label: 'Error',
      className: 'bg-danger/10 text-danger',
    },
  }[status];

  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full',
        config.className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
}

export function IntegrationsTab({ teamId }: IntegrationsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  // State for pending session / account selection
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);

  const { data: connection, isLoading, refetch } = useMetaConnection(teamId);
  const connectMeta = useConnectMeta(teamId);
  const disconnectMeta = useDisconnectMeta(teamId);

  // Account selection hooks
  const { data: pendingAccounts, isLoading: isLoadingAccounts } = useMetaAccounts(pendingSessionId);
  const finalizeConnection = useFinalizeMetaConnection(teamId);
  const cancelPendingSession = useCancelPendingSession();

  // Handle OAuth callback status from URL params
  useEffect(() => {
    const status = searchParams.get('status');
    const message = searchParams.get('message');

    if (status === 'success') {
      toast.success(
        'Meta Connected',
        'Your Meta ad account has been connected successfully.'
      );
      refetch();
    } else if (status === 'error') {
      toast.error(
        'Connection Failed',
        message || 'Failed to connect Meta account. Please try again.'
      );
    } else if (status === 'pending_selection') {
      const sessionId = searchParams.get('sessionId');
      if (sessionId) {
        setPendingSessionId(sessionId);
        setIsAccountSelectorOpen(true);
      } else {
        toast.error('Connection Error', 'Missing session information. Please try again.');
      }
    }

    // Clear URL params after handling
    if (status) {
      searchParams.delete('status');
      searchParams.delete('message');
      searchParams.delete('sessionId');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, toast, refetch]);

  const handleConnect = () => {
    // If already connected, show confirmation for replacement
    if (connection && connection.status === 'active') {
      if (
        !confirm(
          'Replace existing Meta connection? This will disconnect the current account.'
        )
      ) {
        return;
      }
    }
    connectMeta.mutate();
  };

  const handleDisconnect = () => {
    if (!confirm('Disconnect Meta? Imported creatives will stop syncing.')) {
      return;
    }
    disconnectMeta.mutate(undefined, {
      onSuccess: () => {
        toast.success('Disconnected', 'Meta account has been disconnected.');
      },
      onError: (error) => {
        toast.error(
          'Failed to disconnect',
          error instanceof Error ? error.message : 'Please try again.'
        );
      },
    });
  };

  const handleAccountSelect = async (accountId: string) => {
    if (!pendingSessionId) return;

    try {
      await finalizeConnection.mutateAsync({ sessionId: pendingSessionId, accountId });
      setIsAccountSelectorOpen(false);
      setPendingSessionId(null);
      toast.success(
        'Meta Connected',
        'Your Meta ad account has been connected successfully.'
      );
    } catch (error) {
      toast.error(
        'Connection Failed',
        error instanceof Error ? error.message : 'Failed to connect Meta account.'
      );
    }
  };

  const handleAccountSelectorClose = async () => {
    if (pendingSessionId) {
      // Clean up pending session on cancel
      try {
        await cancelPendingSession.mutateAsync(pendingSessionId);
      } catch {
        // Ignore cleanup errors
      }
    }
    setIsAccountSelectorOpen(false);
    setPendingSessionId(null);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-brand animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-primary mb-1">
            Integrations
          </h2>
          <p className="text-sm text-secondary">
            Connect external platforms to import creatives and metrics.
          </p>
        </div>

        {/* Meta Ads Section */}
        <div className="border border-border-subtle rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {/* Meta Logo Placeholder */}
              <div className="w-10 h-10 rounded-lg bg-[#1877F2] flex items-center justify-center">
                <span className="text-white font-bold text-lg">f</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary">Meta Ads</h3>
                <p className="text-xs text-secondary mt-0.5">
                  Import video ads and performance metrics from Meta Ads Manager
                </p>
              </div>
            </div>

            {connection && connection.status !== 'revoked' && (
              <StatusBadge status={connection.status} />
            )}
          </div>

          {/* Connection Details or Connect Button */}
          {connection && connection.status !== 'revoked' ? (
            <div className="mt-4 pt-4 border-t border-border-subtle">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-primary">
                    <span className="font-medium">
                      {connection.metaUserName}
                    </span>
                  </p>
                  <p className="text-xs text-secondary">
                    Ad Account: {connection.metaAdAccountName}
                  </p>
                  {connection.tokenExpiresAt && (
                    <p className="text-xs text-muted">
                      Token expires:{' '}
                      {new Date(connection.tokenExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                  {connection.status === 'error' && connection.lastError && (
                    <p className="text-xs text-danger mt-2">
                      {connection.lastError}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  {connection.status === 'expired' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleConnect}
                      loading={connectMeta.isPending}
                      leftIcon={<Link2 className="h-4 w-4" />}
                    >
                      Reconnect
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnect}
                    loading={disconnectMeta.isPending}
                    leftIcon={<Link2Off className="h-4 w-4" />}
                  >
                    Disconnect
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t border-border-subtle">
              <Button
                onClick={handleConnect}
                loading={connectMeta.isPending}
                leftIcon={<Link2 className="h-4 w-4" />}
              >
                Connect Meta Ads
              </Button>
            </div>
          )}
        </div>

        {/* Placeholder for future integrations */}
        <div className="border border-border-subtle border-dashed rounded-lg p-4 opacity-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-surface-subtle flex items-center justify-center">
              <span className="text-secondary font-bold text-lg">+</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-secondary">
                More Integrations Coming Soon
              </h3>
              <p className="text-xs text-muted mt-0.5">
                TikTok, YouTube, and more
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Selector Modal */}
      <AccountSelectorModal
        isOpen={isAccountSelectorOpen}
        onClose={handleAccountSelectorClose}
        onSelect={handleAccountSelect}
        accounts={pendingAccounts || []}
        isLoading={isLoadingAccounts}
        isConnecting={finalizeConnection.isPending}
      />
    </Card>
  );
}

export default IntegrationsTab;
